if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        
        // Initialize cart with better error handling
        const cartElement = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const cartItemsElement = document.querySelector('cart-drawer-items');
        
        // Check if we have a cart element and either it has the method or we have a cart-items element
        if (cartElement && (typeof cartElement.getSectionsToRender === 'function' || cartItemsElement)) {
          this.cart = cartElement;
        } else if (cartElement) {
          // Only show warning if we found a cart element but it's not properly initialized
          console.warn('Cart element found but missing required methods. Falling back to page reload.');
          this.cart = null;
        } else {
          // No cart element found at all
          this.cart = null;
        }
        
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          try {
            const sections = this.cart.getSectionsToRender();
            if (Array.isArray(sections)) {
              formData.append(
                'sections',
                sections.map((section) => section.id)
              );
              formData.append('sections_url', window.location.pathname);
            }
            if (typeof this.cart.setActiveElement === 'function') {
              this.cart.setActiveElement(document.activeElement);
            }
          } catch (error) {
            console.error('Error in cart update:', error);
            // Fall back to page reload if cart update fails
            window.location = window.routes.cart_url;
            return;
          }
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            }).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            });

            const quickAddModal = this.closest('quick-add-modal');
            // Open the cart drawer after adding an item
            const openCartDrawer = () => {
              console.log('Attempting to open cart drawer...');
              
              // First, try to find the cart drawer element
              let cartDrawer = document.querySelector('cart-drawer');
              
              // If cart drawer doesn't exist, try to initialize it
              if (!cartDrawer) {
                console.log('Cart drawer not found, checking if it needs to be initialized...');
                
                // Check if the cart drawer is in the DOM but not upgraded yet
                const cartDrawerElement = document.querySelector('cart-drawer:not(:defined)');
                if (cartDrawerElement) {
                  console.log('Cart drawer element exists but not upgraded, waiting for upgrade...');
                  customElements.whenDefined('cart-drawer').then(() => {
                    console.log('Cart drawer upgraded, attempting to open...');
                    document.querySelector('cart-drawer')?.open();
                  }).catch(error => {
                    console.error('Error waiting for cart-drawer upgrade:', error);
                    window.location.href = '/cart';
                  });
                  return;
                } else {
                  console.log('No cart drawer element found in DOM, redirecting to cart page');
                  window.location.href = '/cart';
                  return;
                }
              }
              
              // If we have the cart drawer, try to open it
              try {
                console.log('Found cart drawer, attempting to open...');
                
                // Check if the drawer is already open
                if (cartDrawer.hasAttribute('open')) {
                  console.log('Cart drawer is already open');
                  return;
                }
                
                // Try to open the drawer
                if (typeof cartDrawer.open === 'function') {
                  console.log('Using cartDrawer.open()');
                  cartDrawer.open();
                } else if (typeof cartDrawer.openDrawer === 'function') {
                  console.log('Using cartDrawer.openDrawer()');
                  cartDrawer.openDrawer();
                } else if (cartDrawer.hasAttribute('open')) {
                  console.log('Cart drawer already open');
                } else {
                  console.log('No open methods found, trying to dispatch event...');
                  // Try to dispatch a custom event that the cart drawer might be listening for
                  const event = new CustomEvent('open-cart-drawer');
                  document.dispatchEvent(event);
                  
                  // If that doesn't work, try to click the cart icon
                  setTimeout(() => {
                    if (!document.querySelector('cart-drawer[open]')) {
                      console.log('Custom event did not work, trying to click cart icon...');
                      const cartIcon = document.querySelector('a[href="/cart"]') || 
                                    document.querySelector('#cart-icon-bubble') ||
                                    document.querySelector('.header__icon--cart');
                      if (cartIcon) {
                        cartIcon.click();
                      } else {
                        console.log('Cart icon not found, redirecting to cart page');
                        window.location.href = '/cart';
                      }
                    }
                  }, 100);
                }
              } catch (error) {
                console.error('Error opening cart drawer:', error);
                window.location.href = '/cart';
              }
            };

            const updateCartUI = () => {
              try {
                if (this.cart && typeof this.cart.renderContents === 'function' && 
                    response && response.sections && response.sections['cart-drawer']) {
                  CartPerformance.measure("add:paint-updated-sections", () => {
                    this.cart.renderContents(response);
                  });
                } else {
                  console.warn('Cart drawer not properly initialized or missing response data, falling back to page reload');
                  window.location.reload();
                  return;
                }
                openCartDrawer();
              } catch (error) {
                console.error('Error updating cart UI:', error);
                window.location.reload();
              }
            };

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(updateCartUI, 100); // Small delay to ensure modal is fully closed
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              updateCartUI();
            }
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }


      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
